import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Plus,
  CreditCard,
  Pencil,
  Trash2,
  Loader2,
  ArrowRight,
  X,
} from "lucide-react";
import { generalCategoriesAPI, generalSubscriptionAPI } from "../../services/generalService";

const PLAN_TYPE_LABELS = {
  MONTHLY: "شهر واحد",
  QUARTERLY: "ثلاثة أشهر",
};

const emptyForm = {
  plan_type: "MONTHLY",
  price: "",
  duration_days: "",
  description: "",
  is_active: true,
};

export default function GeneralCategoryPlans() {
  const navigate = useNavigate();
  const { categoryId } = useParams();

  const [category, setCategory] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [categoryId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [categoryData, plansData] = await Promise.all([
        generalCategoriesAPI.getById(categoryId),
        generalSubscriptionAPI.getAllPlansAdmin(categoryId),
      ]);
      setCategory(categoryData);
      setPlans(plansData.plans || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingPlan(null);
    setForm(emptyForm);
    setError("");
    setModalOpen(true);
  };

  const openEditModal = (plan) => {
    setEditingPlan(plan);
    setForm({
      plan_type: plan.plan_type,
      price: plan.price,
      duration_days: plan.duration_days,
      description: plan.description || "",
      is_active: plan.is_active,
    });
    setError("");
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      if (editingPlan) {
        await generalSubscriptionAPI.updatePlan(editingPlan.id, {
          price: form.price,
          duration_days: form.duration_days,
          description: form.description,
          is_active: form.is_active,
        });
      } else {
        await generalSubscriptionAPI.createPlan(categoryId, form);
      }
      setModalOpen(false);
      fetchData();
    } catch (err) {
      setError(
        err?.rgeneralonse?.data?.error || "حدث خطأ، حاول مرة أخرى"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    try {
      setDeleting(true);
      await generalSubscriptionAPI.deletePlan(deleteModal.id);
      setDeleteModal(null);
      fetchData();
    } catch (err) {
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/dashboard/general/categories")}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowRight className="w-5 h-5 text-gray-600" />
          </button>
          <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-emerald-700" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              خطط اشتراك — {category?.name}
            </h1>
            <p className="text-sm text-gray-500">{plans.length} خطة</p>
          </div>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2.5 rounded-xl hover:bg-emerald-700 transition-colors font-medium"
        >
          <Plus className="w-4 h-4" />
          إضافة خطة
        </button>
      </div>

      {/* Plans List */}
      {plans.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>لا توجد خطط لهذه الكاتيجوري بعد</p>
          <button
            onClick={openCreateModal}
            className="mt-4 inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium"
          >
            <Plus className="w-4 h-4" />
            أضف خطة الآن
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className="bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between mb-3">
                <span
                  className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                    plan.is_active
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {plan.is_active ? "مفعّلة" : "معطّلة"}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEditModal(plan)}
                    className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeleteModal(plan)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <h3 className="font-bold text-gray-900 mb-1">
                {PLAN_TYPE_LABELS[plan.plan_type] || plan.plan_type}
              </h3>
              <p className="text-2xl font-bold text-emerald-700 mb-2">
                {plan.price} <span className="text-sm font-normal text-gray-500">ريال</span>
              </p>
              <p className="text-sm text-gray-500 mb-2">
                مدة الاشتراك: {plan.duration_days} يوم
              </p>
              {plan.description && (
                <p className="text-sm text-gray-400 pt-2 border-t border-gray-100">
                  {plan.description}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl relative">
            <button
              onClick={() => setModalOpen(false)}
              className="absolute top-4 left-4 p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {editingPlan ? "تعديل الخطة" : "إضافة خطة جديدة"}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!editingPlan && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    نوع الخطة
                  </label>
                  <select
                    value={form.plan_type}
                    onChange={(e) =>
                      setForm({ ...form, plan_type: e.target.value })
                    }
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="MONTHLY">شهر واحد</option>
                    <option value="QUARTERLY">ثلاثة أشهر</option>
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  السعر (ريال)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  مدة الاشتراك (أيام)
                </label>
                <input
                  type="number"
                  value={form.duration_days}
                  onChange={(e) =>
                    setForm({ ...form, duration_days: e.target.value })
                  }
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  الوصف (اختياري)
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  rows={2}
                />
              </div>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) =>
                    setForm({ ...form, is_active: e.target.checked })
                  }
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm text-gray-700">مفعّلة</span>
              </label>

              {error && (
                <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 font-medium"
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-medium flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  حفظ
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-center text-gray-900 mb-2">
              تعطيل الخطة
            </h3>
            <p className="text-gray-500 text-center text-sm mb-6">
              متأكد إنك عاوز تعطل خطة{" "}
              <span className="font-semibold text-gray-700">
                "{PLAN_TYPE_LABELS[deleteModal.plan_type]}"
              </span>
              ؟
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteModal(null)}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 font-medium"
              >
                إلغاء
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                تعطيل
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}